import Box from 'assets/components/box/Box';
import DataMap from 'assets/components/dataMap/DataMap';
import Icon from 'assets/components/icon/Icon';
import ErrorWrapper from 'assets/components/inputs/wrappers/ErrorWrapper';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import AlertType from 'models/enums/AlertType';

type Props = {
  alerts?: Utils.IAlert[];
};

const alertStyles: Record<
  Model.Enum.AlertType,
  {
    accent: string;
    background: string;
    icon: string;
  }
> = {
  [AlertType.info]: {
    accent: '#2563eb',
    background: 'rgba(37, 99, 235, 0.08)',
    icon: 'fas-info-circle',
  },
  [AlertType.warning]: {
    accent: '#d97706',
    background: 'rgba(217, 119, 6, 0.1)',
    icon: 'fas-exclamation-triangle',
  },
  [AlertType.error]: {
    accent: Theme.Color.error,
    background: 'rgba(220, 38, 38, 0.08)',
    icon: 'fas-times-circle',
  },
  [AlertType.success]: {
    accent: Theme.Color.success,
    background: 'rgba(22, 163, 74, 0.08)',
    icon: 'fas-check-circle',
  },
};

export default function AlertBox({ alerts }: Props) {
  const { lang } = getLocales();

  if (!alerts?.length) return <ErrorWrapper children={lang.noInfo} />;

  return (
    <Box
      style={{
        display: 'grid',
        gap: Theme.Size.M,
        marginLeft: Theme.Size.XL,
        marginRight: Theme.Size.XL,
      }}
    >
      <DataMap
        data={alerts}
        render={({ data: alert, index }) => {
          const styles = alertStyles[alert?.type as Model.Enum.AlertType] ?? alertStyles[AlertType.info];

          return (
            <Box
              key={`${alert?.type}-${alert?.title}-${index}`}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: Theme.Size.M,
                minWidth: 0,
                padding: Theme.Size.M,
                border: `1px solid ${styles.accent}`,
                borderLeft: `6px solid ${styles.accent}`,
                borderRadius: '8px',
                backgroundColor: styles.background,
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.06)',
                color: Theme.Color.text,
              }}
            >
              <Box
                style={{
                  display: 'flex',
                  flex: '0 0 40px',
                  width: '40px',
                  height: '40px',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: Theme.Color.background,
                  color: styles.accent,
                  fontSize: Theme.FontSize.L,
                }}
              >
                <Icon class={styles.icon} />
              </Box>
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: Theme.Size.XS,
                  minWidth: 0,
                }}
              >
                {alert?.title && (
                  <strong
                    style={{
                      color: styles.accent,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {alert.title}
                  </strong>
                )}
                {alert?.message && (
                  <span
                    style={{
                      lineHeight: 1.45,
                      overflowWrap: 'anywhere',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {alert.message}
                  </span>
                )}
              </Box>
            </Box>
          );
        }}
      />
    </Box>
  );
}
